import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Home } from "lucide-react";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";

/**
 * Shared layout for legal/long-form pages (Privacy, Terms).
 * Provides the navbar, branded page header, content slot, and footer.
 */
export default function LegalLayout({
    title,
    eyebrow,
    lastUpdated,
    intro,
    children,
    testId,
}) {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <main
            data-testid={testId}
            className="relative min-h-screen text-white font-body antialiased"
        >
            <Navbar />

            <section className="relative pt-36 pb-16 luxe-radial overflow-hidden">
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 lg:px-16">
                    <button
                        data-testid="legal-home-btn-top"
                        onClick={() => navigate("/")}
                        className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#C4C4C4] hover:text-[#D4AF37] transition-colors duration-300 mb-12"
                    >
                        <Home className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Return Home
                    </button>

                    <div className="flex items-center gap-3 mb-6">
                        <div className="gold-rule-solid w-10" />
                        <span className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] font-semibold">
                            {eyebrow}
                        </span>
                    </div>

                    <h1
                        data-testid="legal-headline"
                        className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-white font-light"
                    >
                        {title}
                    </h1>

                    {lastUpdated ? (
                        <p className="mt-8 text-[11px] uppercase tracking-[0.32em] text-[#888]">
                            Last Updated · {lastUpdated}
                        </p>
                    ) : null}

                    {intro ? (
                        <p className="mt-8 max-w-3xl text-base md:text-lg font-light leading-relaxed text-[#C4C4C4]">
                            {intro}
                        </p>
                    ) : null}
                </div>
            </section>

            <section className="relative pb-24 md:pb-32 luxe-radial-faint">
                <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16">
                    <article className="legal-content space-y-12 text-[#C4C4C4]">
                        {children}
                    </article>

                    <div className="mt-20 flex justify-center">
                        <button
                            data-testid="legal-home-btn-bottom"
                            onClick={() => navigate("/")}
                            className="group inline-flex items-center gap-3 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/[0.08] hover:border-[#D4AF37] px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-all duration-300"
                        >
                            <Home className="w-4 h-4" strokeWidth={1.5} />
                            Return Home
                        </button>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}

export function LegalSection({ heading, children, testId }) {
    return (
        <section data-testid={testId} className="space-y-4">
            <h2 className="font-display text-2xl md:text-3xl text-white tracking-tight leading-tight">
                {heading}
            </h2>
            <div className="hairline-divider" />
            <div className="space-y-4 text-sm md:text-base font-light leading-relaxed">
                {children}
            </div>
        </section>
    );
}

export function LegalCallout({ children }) {
    return (
        <aside className="relative my-4 border-l-2 border-[#D4AF37] bg-[#050505]/80 p-6 md:p-7">
            <div className="text-sm md:text-base font-light leading-relaxed text-white">
                {children}
            </div>
        </aside>
    );
}
